import { lazy, Suspense, type ReactElement } from 'react';
import { getErrorCopy } from '@/shared/copy/errors';
import type { ResultsFileUploadState } from '@/features/quality/sections/useResultsFileUpload.types';
import type { ProjectDto } from '@flowqa/shared';

const ResultsFileUploadDialog = lazy(async () => {
  const module = await import('@/features/quality/sections/ResultsFileUploadDialog');
  return { default: module.ResultsFileUploadDialog };
});

type EntityTrendsUploadDialogProps = {
  selectedProjectId: string | null;
  projectOptions: ProjectDto[];
  uploadProjectIdFieldId: string;
  uploadFlow: ResultsFileUploadState;
};

const handleUploadConfirm = ({
  selectedProjectId,
  uploadFlow,
}: Pick<EntityTrendsUploadDialogProps, 'selectedProjectId' | 'uploadFlow'>): void => {
  const projectId = selectedProjectId ?? uploadFlow.uploadProjectId;
  if (!projectId) {
    uploadFlow.setUploadError(getErrorCopy('upload.selectProject'));
    return;
  }

  if (uploadFlow.selectedUploadFiles.length === 0) {
    uploadFlow.setUploadError(getErrorCopy('upload.selectSupportedFile'));
    return;
  }

  uploadFlow.setUploadError(null);
  const filesToUpload = uploadFlow.selectedUploadFiles;
  const tagsToUpload = uploadFlow.uploadTagValues;
  const tagColorsToUpload = uploadFlow.uploadTagColors;
  uploadFlow.closeUploadDialog();
  uploadFlow.startUpload(projectId, filesToUpload, tagsToUpload, tagColorsToUpload);
};

export const EntityTrendsUploadDialog = ({
  selectedProjectId,
  projectOptions,
  uploadProjectIdFieldId,
  uploadFlow,
}: EntityTrendsUploadDialogProps): ReactElement | null => {
  if (!uploadFlow.uploadOpen) {
    return null;
  }

  return (
    <Suspense fallback={null}>
      <ResultsFileUploadDialog
        open={uploadFlow.uploadOpen}
        loading={uploadFlow.uploadMutation.isPending}
        confirmDisabled={uploadFlow.disableUpload}
        onClose={uploadFlow.closeUploadDialog}
        onConfirm={() => {
          handleUploadConfirm({ selectedProjectId, uploadFlow });
        }}
        selectedProjectId={selectedProjectId}
        uploadProjectId={uploadFlow.uploadProjectId}
        uploadTags={uploadFlow.uploadTags}
        uploadTagColors={uploadFlow.uploadTagColors}
        onUploadProjectIdChange={uploadFlow.setUploadProjectId}
        onUploadTagsChange={uploadFlow.setUploadTags}
        onUploadTagColorsChange={uploadFlow.setUploadTagColors}
        uploadProjectIdFieldId={uploadProjectIdFieldId}
        projectOptions={projectOptions}
        uploadFiles={uploadFlow.uploadFiles}
        selectedFiles={uploadFlow.selectedFiles}
        isSupportedFile={uploadFlow.isSupportedFile}
        csvHeaderStatusByName={uploadFlow.csvHeaderStatusByName}
        csvHeaderReasonByName={uploadFlow.csvHeaderReasonByName}
        fileValidationStatusByName={uploadFlow.fileValidationStatusByName}
        fileValidationReasonByName={uploadFlow.fileValidationReasonByName}
        handleFilesSelected={uploadFlow.handleFilesSelected}
        setSelectedFiles={uploadFlow.setSelectedFiles}
        setUploadFiles={uploadFlow.setUploadFiles}
        setUploadError={uploadFlow.setUploadError}
        uploadError={uploadFlow.uploadError}
        setCsvHeaderStatusByName={uploadFlow.setCsvHeaderStatusByName}
        setCsvHeaderReasonByName={uploadFlow.setCsvHeaderReasonByName}
        setFileValidationStatusByName={uploadFlow.setFileValidationStatusByName}
        setFileValidationReasonByName={uploadFlow.setFileValidationReasonByName}
      />
    </Suspense>
  );
};
